import { Spinner } from '@tempo-io/sui-spinner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TempoLogo from '@/assets/tempo-logo.svg';
import { Legend } from './Legend';
import { NotFoundMessage } from './NotFoundMessage';
import { PasswordPromptModal } from './PasswordPromptModal';
import { PublishedItemPanel } from './PublishedItemPanel';
import { PublishedSwimlane } from './PublishedSwimlane';
import { PublishedViewAuthProvider, usePublishedViewAuth } from './PublishedViewAuthContext';
import type { PublishedMetadata, PublishingPreferences } from './types';
import { usePublishedSwimlane } from './usePublishedSwimlane';

/**
 * Search parameter schema for published swimlane routes.
 *
 * Optional query parameters for published view customization:
 * - embed: '1' to enable embed mode
 * - export: '1' to enable export mode
 * - title: '0' to hide title
 * - branding: '0' to hide branding/logo
 * - date: '0' to hide date
 * - legend: '0' to hide legend
 * - vision: Custom vision statement text
 *
 * Note: Query parameters can be parsed as numbers by TanStack Router (e.g., ?embed=1)
 * so we use union types to accept both string and number inputs.
 */
export const publishedSwimlaneSearchSchema = z.object({
  embed: z.union([z.string(), z.number()]).optional(),
  export: z.union([z.string(), z.number()]).optional(),
  title: z.union([z.string(), z.number()]).optional(),
  branding: z.union([z.string(), z.number()]).optional(),
  date: z.union([z.string(), z.number()]).optional(),
  legend: z.union([z.string(), z.number()]).optional(),
  vision: z.string().optional(),
});

export type PublishedSwimlaneSearchParams = z.infer<typeof publishedSwimlaneSearchSchema>;

export interface PublishedSwimlanePageProps {
  /**
   * The linkId from the route parameter
   */
  linkId: string;
  /**
   * Search parameters from the URL query string
   */
  searchParams: PublishedSwimlaneSearchParams;
  /**
   * Whether this is being rendered as a PNG publication template.
   * When true, enables features for backend screenshot capture.
   */
  isPngPublicationTemplate?: boolean;
}

/**
 * Helper function to check if a query parameter represents a truthy value.
 * Returns true if the value is '1' or the number 1.
 */
function isQueryParamTruthy(value: string | number | undefined): boolean {
  return value === '1' || value === 1;
}

/**
 * Discriminated union representing the view mode for published swimlanes.
 * - default: Normal published view with standard settings
 * - export: Export mode for PNG screenshot capture (allows scrolling)
 * - embed: Embed mode for iframe embedding (query param overrides)
 */
type ViewMode =
  | { readonly kind: 'default' }
  | { readonly kind: 'export' }
  | { readonly kind: 'embed' };

/**
 * Parses the view mode from URL search parameters.
 * Export mode takes precedence over embed mode if both are specified.
 */
function parseViewMode(searchParams: PublishedSwimlaneSearchParams): ViewMode {
  const hasExport = isQueryParamTruthy(searchParams.export);
  const hasEmbed = isQueryParamTruthy(searchParams.embed);
  if (hasExport) return { kind: 'export' };
  if (hasEmbed) return { kind: 'embed' };
  return { kind: 'default' };
}

/**
 * Behavioral configuration derived from view mode.
 */
interface ViewModeBehavior {
  readonly allowsQueryOverrides: boolean;
  readonly overflowClass: 'overflow-hidden' | '';
}

/**
 * Returns the behavioral configuration for a given view mode.
 * - default: No query overrides, hidden overflow
 * - export: Query overrides allowed, no overflow hiding (for screenshot capture)
 * - embed: Query overrides allowed, hidden overflow
 */
function getViewModeBehavior(mode: ViewMode): ViewModeBehavior {
  switch (mode.kind) {
    case 'default':
      return { allowsQueryOverrides: false, overflowClass: 'overflow-hidden' };
    case 'export':
      return { allowsQueryOverrides: true, overflowClass: '' };
    case 'embed':
      return { allowsQueryOverrides: true, overflowClass: 'overflow-hidden' };
  }
}

/**
 * Helper function to parse boolean query parameters.
 * Returns true if the value is '1' or 1, defaults to the provided default value if undefined.
 */
function getBooleanQueryParam(value: string | number | undefined, defaultValue: boolean): boolean {
  if (value === undefined) return defaultValue;
  return isQueryParamTruthy(value);
}

/**
 * Computes the effective preferences for the published swimlane.
 * In export mode or embed mode, query parameters override the published preferences.
 */
function getEffectivePreferences(
  metadata: PublishedMetadata,
  searchParams: PublishedSwimlaneSearchParams,
  allowsQueryOverrides: boolean,
): PublishingPreferences {
  if (!allowsQueryOverrides) return metadata.preferences;

  return {
    ...metadata.preferences,
    showTitle: getBooleanQueryParam(searchParams.title, true),
    showTempoBranding: getBooleanQueryParam(searchParams.branding, true),
    showPublishDate: getBooleanQueryParam(searchParams.date, true),
    showLegend: getBooleanQueryParam(searchParams.legend, true),
    showVisionStatement:
      searchParams.vision !== undefined ? true : metadata.preferences.showVisionStatement,
  };
}

/**
 * PublishedSwimlanePage - Displays a read-only published swimlane view
 *
 * This is a completely separate component from ViewPage.
 * No flags, no dual responsibility - just displays published swimlanes.
 */
export function PublishedSwimlanePage(props: PublishedSwimlanePageProps) {
  return (
    <PublishedViewAuthProvider>
      <PublishedSwimlanePageContent {...props} />
    </PublishedViewAuthProvider>
  );
}

function PublishedSwimlanePageContent({ linkId, searchParams }: PublishedSwimlanePageProps) {
  const { t } = useTranslation();
  const { submit } = usePublishedViewAuth();
  const result = usePublishedSwimlane(linkId);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Exhaustive switch ensures all states are handled
  switch (result.status) {
    case 'loading':
      return (
        <div className="flex w-full min-h-screen items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-2xl font-bold text-text-neutral-primary">
              {t('views.published.loading')}
            </h1>
            <Spinner size={40} isLoading={true} />
          </div>
        </div>
      );

    case 'error':
      return (
        <div
          className="flex items-center justify-center h-screen text-xl text-text-error"
          role="alert"
        >
          {t('views.published.errorLoading', { message: result.error.message })}
        </div>
      );

    case 'password_required':
      return <PasswordPromptModal authStatus="password_required" onSubmit={submit} />;

    case 'password_invalid':
      return <PasswordPromptModal authStatus="password_invalid" onSubmit={submit} />;

    case 'not_found':
      return <NotFoundMessage />;

    case 'success':
      break; // Fall through to render content
  }

  const { metadata, legendItems, items, itemDetails, hiddenFieldIds } = result.data;

  const viewMode = parseViewMode(searchParams);
  const { allowsQueryOverrides, overflowClass } = getViewModeBehavior(viewMode);
  const preferences = getEffectivePreferences(metadata, searchParams, allowsQueryOverrides);

  const visionStatement =
    allowsQueryOverrides && searchParams.vision !== undefined
      ? searchParams.vision
      : metadata.visionStatement;

  // Find selected item data
  const selectedEnrichedItem = selectedItemId ? (items[selectedItemId] ?? null) : null;
  const selectedItemDetails = selectedItemId ? (itemDetails[selectedItemId] ?? null) : null;

  const handleItemClick = (itemId: string) => {
    setSelectedItemId(itemId);
    setExpanded(true);
  };

  const handleClose = () => {
    setSelectedItemId(null);
    setExpanded(false);
  };

  // Determine if header section should be shown
  const showHeader = preferences.showTitle || preferences.showVisionStatement;

  const rootContainerClass = 'h-screen w-screen bg-bg-surface-base flex flex-col';
  const swimlaneContainerClass = `flex-1 ${overflowClass}`.trim();

  // Format publish date
  const publishDate = new Date(metadata.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className={rootContainerClass}>
      {/* Header Section - Title and Vision Statement */}
      {showHeader && (
        <div className="flex-none px-8 pt-6 pb-4 border-b border-border-neutral">
          {preferences.showTitle && (
            <h1 className="text-2xl font-bold text-text-neutral-primary mb-2">{metadata.title}</h1>
          )}
          {preferences.showVisionStatement && visionStatement && (
            <div className="max-h-32 overflow-y-auto">
              <p className="text-sm text-text-neutral-secondary whitespace-pre-wrap">
                {visionStatement}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Swimlane Content */}
      <div className={swimlaneContainerClass}>
        <PublishedSwimlane data={result.data} onItemClick={handleItemClick} />
      </div>

      {/* Item Panel */}
      <PublishedItemPanel
        itemDetails={selectedItemDetails}
        enrichedItem={selectedEnrichedItem}
        hiddenFieldIds={hiddenFieldIds}
        expanded={expanded}
        onExpandedChange={setExpanded}
        onClose={handleClose}
      />

      {/* Footer Section - Publish Date, Branding, and Legend */}
      {(preferences.showPublishDate || preferences.showTempoBranding || preferences.showLegend) && (
        <div className="flex-none py-4 w-full border-t border-border-neutral">
          <div className="flex items-center justify-between px-8">
            <div className="text-sm text-text-neutral-secondary">
              {preferences.showPublishDate && <span>Published on {publishDate}</span>}
            </div>
            <div className="flex items-center gap-3">
              {preferences.showTempoBranding && (
                <div className="flex items-center gap-2">
                  <img src={TempoLogo} alt="Tempo" className="h-6 w-6" />
                  <span className="text-sm font-semibold text-text-neutral-primary">Tempo SPM</span>
                </div>
              )}
              {preferences.showLegend && <Legend items={legendItems} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
